import React, { ReactElement } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  HashRouter as Router,
  Routes,
  Navigate,
  Route,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { RecoilRoot } from 'recoil';

import Main from '@views/main'
import Handball from '@views/handball'
import InvestmentsDashboard from '@views/investments'
import RealEstateWorkspaceView from '@views/real-estate'
import EconomicsView from '@views/economics'
import EconomicsOverviewScreen from '@features/economics/screens/overview-screen';
import EconomicsHistoryScreen from '@features/economics/screens/history-screen';
import EconomicsCalendarScreen from '@features/economics/screens/calendar-screen';
import EconomicsSystemScreen from '@features/economics/screens/system-screen';
import EconomicsScenariosScreen from '@features/economics/screens/scenarios-screen';
import InvestmentsCoverageView from '@views/investments/coverage'
import InvestmentsMacroView from '@views/investments/macro'
import InvestmentsGlossaryView from '@views/investments/reference-glossary'
import InvestmentsMethodView from '@views/investments/reference-method'
import InvestmentsHelpView from '@views/investments/reference-help'
import InvestmentTickerView from '@views/investments/ticker'
import InvestmentETFTickerView from '@views/investments/etf'
import InvestmentsPortfolioView from '@views/investments/portfolio'
import InvestmentsOperatorView from '@views/investments/operator'
import SwingTradingNewsView from '@views/swing-trading/news'
import TradingChecklist from "@features/trading-checklist/screen";
import SwingTradingView from '@views/swing-trading'
import SwingTradingFocusView from '@views/swing-trading/focus'
import SwingTradingSymbolView from '@views/swing-trading/symbol'
import SwingTradingPlansView from '@views/swing-trading/plans'
import SwingTradingTradesView from '@views/swing-trading/trades'
import SwingTradingJournalView from '@views/swing-trading/journal'
import SwingTradingSettingsView from '@views/swing-trading/settings'
import SwingTradingOperatorView from '@views/swing-trading/operator'
import SwingTradingScannerView from '@views/swing-trading/scanner'
import RealEstateIntakeScreen from '@features/real-estate/screens/intake-screen';
import RealEstateContextScreen from '@features/real-estate/screens/context-screen';
import RealEstateUnderwriteScreen from '@features/real-estate/screens/underwrite-screen';
import RealEstateResultsScreen from '@features/real-estate/screens/results-screen';
import { useInvestmentsAccessQuery } from '@features/investments/queries';
import { trackPageView } from '@internal/utils/analytics';

const queryClient = new QueryClient();

type InvestmentsRouteGuardProps = {
  children: ReactElement;
  requireGod?: boolean;
};

const InvestmentsRouteGuard = ({ children, requireGod = false }: InvestmentsRouteGuardProps): ReactElement => {
  const location = useLocation();
  const navigate = useNavigate();
  const accessQuery = useInvestmentsAccessQuery();
  const hasSiteAccess = accessQuery.data?.authenticated === true && accessQuery.data?.authorized === true;
  const authorized = hasSiteAccess && (!requireGod || accessQuery.data?.isGod === true);

  React.useEffect(() => {
    const access = accessQuery.data;
    if (!access && !accessQuery.error) {
      return;
    }
    if (accessQuery.error || !access?.authenticated || access.reason === "unauthenticated" || access.reason === "session_invalid") {
      const nextPath = `/#${location.pathname}${location.search || ""}`;
      window.location.href = `/api/auth/login/google?next=${encodeURIComponent(nextPath)}`;
      return;
    }
    if (!access.authorized) {
      navigate("/", { replace: true });
      return;
    }
    if (requireGod && !access.isGod) {
      navigate("/investments/coverage", { replace: true });
    }
  }, [accessQuery.data, accessQuery.error, location.pathname, location.search, navigate, requireGod]);

  if (!authorized) {
    return <div>Checking access...</div>;
  }
  return children;
};

const InvestmentsHomeRoute = (): ReactElement => {
  const accessQuery = useInvestmentsAccessQuery();

  if (accessQuery.isLoading || !accessQuery.data) {
    return <div>Checking access...</div>;
  }

  if (!accessQuery.data.isGod) {
    return <Navigate to="/investments/coverage" replace />;
  }

  return <InvestmentsDashboard />;
};

const LegacyGlossaryRedirect = (): ReactElement => {
  const params = useParams();
  return <Navigate to={params.slug ? `/investments/reference/glossary/${params.slug}` : "/investments/reference/glossary"} replace />;
};

export const AnalyticsPageViews = (): ReactElement | null => {
  const location = useLocation();

  React.useEffect(() => {
    if (location.pathname.startsWith("/swing-trading") || location.pathname.startsWith("/trading-checklist")) {
      return undefined;
    }
    const timeoutID = window.setTimeout(() => {
      trackPageView({
        pageTitle: document.title,
        pagePath: `${location.pathname}${location.search || ""}`,
        pageLocation: window.location.href,
      });
    }, 75);

    return () => {
      window.clearTimeout(timeoutID);
    };
  }, [location.pathname, location.search]);

  return null;
};

const App = (): ReactElement => {
  return (
    <RecoilRoot>
      <QueryClientProvider client={queryClient}>
        <Router>
          <AnalyticsPageViews />
          <Routes>
            <Route path="/" element={<Main />} />
            <Route path="/trading-checklist" element={<InvestmentsRouteGuard requireGod><TradingChecklist /></InvestmentsRouteGuard>} />
            <Route path="/handball" element={<Handball />} />
            <Route path="/handball/rankings" element={<Handball />} />
            <Route path="/handball/tournaments" element={<Handball />} />
            <Route path="/handball/tournaments/:id" element={<Handball />} />
            <Route path="/real-estate" element={<RealEstateWorkspaceView />}>
              <Route index element={<Navigate to="intake" replace />} />
              <Route path="intake" element={<RealEstateIntakeScreen />} />
              <Route path="context" element={<RealEstateContextScreen />} />
              <Route path="underwrite" element={<RealEstateUnderwriteScreen />} />
              <Route path="results" element={<RealEstateResultsScreen />} />
              <Route path="*" element={<Navigate to="intake" replace />} />
            </Route>
            <Route path="/economics" element={<EconomicsView />}>
              <Route index element={<EconomicsOverviewScreen />} />
              <Route path="history" element={<EconomicsHistoryScreen />} />
              <Route path="calendar" element={<EconomicsCalendarScreen />} />
              <Route path="system" element={<EconomicsSystemScreen />} />
              <Route path="scenarios" element={<EconomicsScenariosScreen />} />
              <Route path="*" element={<Navigate to="/economics" replace />} />
            </Route>
            <Route
              path="/investments"
              element={(
                <InvestmentsRouteGuard>
                  <InvestmentsHomeRoute />
                </InvestmentsRouteGuard>
              )}
            />
            <Route
              path="/investments/coverage"
              element={(
                <InvestmentsRouteGuard>
                  <InvestmentsCoverageView />
                </InvestmentsRouteGuard>
              )}
            />
            <Route
              path="/investments/macro"
              element={(
                <InvestmentsRouteGuard>
                  <InvestmentsMacroView />
                </InvestmentsRouteGuard>
              )}
            />
            <Route
              path="/investments/reference/glossary"
              element={(
                <InvestmentsRouteGuard>
                  <InvestmentsGlossaryView />
                </InvestmentsRouteGuard>
              )}
            />
            <Route
              path="/investments/reference/glossary/:slug"
              element={(
                <InvestmentsRouteGuard>
                  <InvestmentsGlossaryView />
                </InvestmentsRouteGuard>
              )}
            />
            <Route
              path="/investments/reference/method"
              element={(
                <InvestmentsRouteGuard>
                  <InvestmentsMethodView />
                </InvestmentsRouteGuard>
              )}
            />
            <Route
              path="/investments/reference/help"
              element={(
                <InvestmentsRouteGuard>
                  <InvestmentsHelpView />
                </InvestmentsRouteGuard>
              )}
            />
            <Route
              path="/investments/:ticker"
              element={(
                <InvestmentsRouteGuard>
                  <InvestmentTickerView />
                </InvestmentsRouteGuard>
              )}
            />
            <Route
              path="/investments/etf/:ticker"
              element={(
                <InvestmentsRouteGuard>
                  <InvestmentETFTickerView />
                </InvestmentsRouteGuard>
              )}
            />
            <Route
              path="/investments/portfolio"
              element={(
                <InvestmentsRouteGuard requireGod>
                  <InvestmentsPortfolioView />
                </InvestmentsRouteGuard>
              )}
            />
            <Route
              path="/investments/operator"
              element={(
                <InvestmentsRouteGuard requireGod>
                  <InvestmentsOperatorView />
                </InvestmentsRouteGuard>
              )}
            />
            <Route
              path="/swing-trading"
              element={(
                <InvestmentsRouteGuard requireGod>
                  <SwingTradingView />
                </InvestmentsRouteGuard>
              )}
            />
            <Route path="/swing-trading/focus" element={<InvestmentsRouteGuard requireGod><SwingTradingFocusView /></InvestmentsRouteGuard>} />
            <Route path="/swing-trading/scanner" element={<InvestmentsRouteGuard requireGod><SwingTradingScannerView /></InvestmentsRouteGuard>} />
            <Route path="/swing-trading/symbols/:ticker" element={<InvestmentsRouteGuard requireGod><SwingTradingSymbolView /></InvestmentsRouteGuard>} />
            <Route path="/swing-trading/plans" element={<InvestmentsRouteGuard requireGod><SwingTradingPlansView /></InvestmentsRouteGuard>} />
            <Route path="/swing-trading/trades" element={<InvestmentsRouteGuard requireGod><SwingTradingTradesView /></InvestmentsRouteGuard>} />
            <Route path="/swing-trading/journal" element={<InvestmentsRouteGuard requireGod><SwingTradingJournalView /></InvestmentsRouteGuard>} />
            <Route path="/swing-trading/settings" element={<InvestmentsRouteGuard requireGod><SwingTradingSettingsView /></InvestmentsRouteGuard>} />
            <Route path="/swing-trading/operator" element={<InvestmentsRouteGuard requireGod><SwingTradingOperatorView /></InvestmentsRouteGuard>} />
            <Route
              path="/swing-trading/news"
              element={(
                <InvestmentsRouteGuard requireGod>
                  <SwingTradingNewsView />
                </InvestmentsRouteGuard>
              )}
            />
            <Route path="/investments/research" element={<Navigate to="/investments/coverage" replace />} />
            <Route path="/investments/rates" element={<Navigate to="/investments/macro" replace />} />
            <Route path="/investments/etfs" element={<Navigate to="/investments/coverage" replace />} />
            <Route path="/investments/glossary" element={<LegacyGlossaryRedirect />} />
            <Route path="/investments/glossary/:slug" element={<LegacyGlossaryRedirect />} />
            <Route path="/investments/watchlist" element={<Navigate to="/investments/portfolio" replace />} />
            <Route path="/*" element={<Navigate to="/" />} />
          </Routes>
        </Router>
      </QueryClientProvider>
    </RecoilRoot>
  );
};

export default App;
